import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { apiService } from "service/ApiService";
import { notify } from "service/NotificationService";

// components

import Pagination from "components/Pagination/Pagination";
import CompanySelector from "components/Selectors/CompanySelector";
import LoadButton from "components/Buttons/LoadButton";
import ExternalLinkButton from "components/Buttons/ExternalLinkButton";

export default function InvoicesTable({ color }) {
  const [companyId, setCompanyId] = useState(null);
  const [data, setData] = useState([]);
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [dataPerPage, setDataPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);

  const handleError = (error) => notify("Error", error);

  const fetchData = async () => {
    try {
      if (!companyId) {
        handleError("Please select a company first.");
        return;
      }
      setBtnDisabled(true);
      const dataResponse = await apiService.get(`api/invoices/company/${companyId}`);
      setData(dataResponse);
      setBtnDisabled(false);
    } catch (error) {
      console.error("Error fetching data:", error.message);
      handleError(error.message);
      setBtnDisabled(false);
    }
  };

  const startIndex = currentPage * dataPerPage;
  const paginatedData = data.slice(startIndex, startIndex + dataPerPage);

  return (
    <>
      <div
        className={
          "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
          (color === "light" ? "bg-white" : "bg-lightBlue-900 text-white")
        }
      >
        <div className="rounded-t mb-0 px-4 py-3 border-0">
          <div className="flex flex-wrap">
            <div className="w-full px-4 flex-1">
              <h3 className={"font-semibold text-lg " + (color === "light" ? "text-blueGray-700" : "text-white")}>Invoices</h3>
            </div>
            <div className="w-full px-4 flex-1 md:2/12 md:flex-none">
              <CompanySelector defaultValue={companyId} handler={setCompanyId} />
            </div>
            <div>
              <LoadButton handler={fetchData} disabled={btnDisabled} />
            </div>
          </div>
        </div>
        <div className="block w-full overflow-x-auto">
          {/* Projects table */}
          <table className="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr>
                <th className="px-6 align-middle border border-solid py-3 uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                  NUM
                </th>
                <th className="px-6 align-middle border border-solid py-3 uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                  MARK
                </th>
                <th className="px-6 align-middle border border-solid py-3 uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                  CREATED DATE
                </th>
                <th className="px-6 align-middle border border-solid py-3 uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                  VAT AMOUNT (€)
                </th>
                <th className="px-6 align-middle border border-solid py-3 uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                  GROSS AMOUNT (€)
                </th>
                <th className="px-6 align-middle border border-solid py-3 uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100"></th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((invoice, index) => (
                <tr key={index}>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 whitespace-nowrap p-4">{invoice.invoice.header.number}</td>
                  <th className="border-t-0 px-6 align-middle border-l-0 border-r-0 whitespace-nowrap p-4 text-left items-center">{invoice.response.mark}</th>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 whitespace-nowrap p-4">{invoice.createdDate}</td>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 whitespace-nowrap p-4">
                    {invoice.invoice.summary.total_vat_amount.toFixed(2)}
                  </td>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 whitespace-nowrap p-4">
                    {invoice.invoice.summary.total_gross_amount.toFixed(2)}
                  </td>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 whitespace-nowrap p-4 text-right">
                    <ExternalLinkButton title="View on provider" url={invoice.response.url} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          totalRows={data.length}
          dataPerPage={dataPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          setDataPerPage={setDataPerPage}
        />
      </div>
    </>
  );
}

InvoicesTable.defaultProps = {
  color: "light",
};

InvoicesTable.propTypes = {
  color: PropTypes.oneOf(["light", "dark"]),
};
